import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getSites,
  getSiteById,
  createSite,
  updateSite,
  deleteSite,
  uploadSiteFloorplan,
  getSiteFloorplan,
  deleteSiteFloorplan,
  listSiteBlueprints,
  createSiteBlueprint,
  updateSiteBlueprint,
  replaceSiteBlueprintFile,
  deleteSiteBlueprint,
  type SiteFilters,
  type Site,
  type SiteBlueprint,
} from '../../services';

// Query keys factory - prevents typos and enables smart invalidation
export const siteKeys = {
  all: ['sites'] as const,
  lists: () => [...siteKeys.all, 'list'] as const,
  list: (filters: SiteFilters) => [...siteKeys.lists(), filters] as const,
  details: () => [...siteKeys.all, 'detail'] as const,
  detail: (id: number) => [...siteKeys.details(), id] as const,
  floorplan: (siteId: number) =>
    [...siteKeys.all, 'floorplan', siteId] as const,
};

// List query with filters and pagination
export function useSites(filters: SiteFilters) {
  return useQuery({
    queryKey: siteKeys.list(filters),
    queryFn: () => getSites(filters),
    placeholderData: (previousData) => previousData, // Keep previous data while fetching new page
  });
}

// Single site query
export function useSite(id: number) {
  return useQuery({
    queryKey: siteKeys.detail(id),
    queryFn: () => getSiteById(id),
    enabled: !!id,
  });
}

// Create mutation
export function useCreateSite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createSite,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: siteKeys.lists() });
    },
  });
}

// Update mutation
export function useUpdateSite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Site> }) =>
      updateSite(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: siteKeys.lists() });
      queryClient.invalidateQueries({ queryKey: siteKeys.detail(id) });
    },
  });
}

// Delete mutation
export function useDeleteSite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteSite,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: siteKeys.lists() });
    },
  });
}

// Floorplan query with polling for licensed sites during processing
export function useSiteFloorplan(siteId: number, licensed = false) {
  return useQuery({
    queryKey: siteKeys.floorplan(siteId),
    queryFn: () => getSiteFloorplan(siteId),
    enabled: !!siteId,
    refetchInterval: (query) => {
      if (!licensed) return false;
      const status = query.state.data?.status;
      return status === 'pending' || status === 'processing' ? 5000 : false;
    },
  });
}

// Upload floorplan mutation
export function useUploadSiteFloorplan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      siteId,
      file,
      licensed,
    }: {
      siteId: number;
      file: File;
      licensed: boolean;
    }) => uploadSiteFloorplan(siteId, file, licensed),
    onSuccess: (_, { siteId }) => {
      queryClient.invalidateQueries({ queryKey: siteKeys.floorplan(siteId) });
      queryClient.invalidateQueries({ queryKey: siteKeys.detail(siteId) });
    },
  });
}

// Delete floorplan mutation
export function useDeleteSiteFloorplan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (siteId: number) => deleteSiteFloorplan(siteId),
    onSuccess: (_, siteId) => {
      queryClient.invalidateQueries({ queryKey: siteKeys.floorplan(siteId) });
      queryClient.invalidateQueries({ queryKey: siteKeys.detail(siteId) });
    },
  });
}

// --- Site Blueprints (multi-artifact, ad-hoc per site) --------------------

export const blueprintKeys = {
  all: ['site-blueprints'] as const,
  list: (siteId: number) => [...blueprintKeys.all, 'list', siteId] as const,
};

/**
 * List blueprints for a site. Polls every 5s while any artifact is still
 * Pending or Submitted so the lifecycle transitions show up automatically;
 * goes silent once everything is Complete or Failed.
 */
export function useSiteBlueprints(siteId: number, enabled = true) {
  return useQuery({
    queryKey: blueprintKeys.list(siteId),
    queryFn: () => listSiteBlueprints(siteId),
    enabled: enabled && !!siteId,
    refetchInterval: (query) => {
      const rows = query.state.data;
      if (!rows || rows.length === 0) return false;
      const inFlight = rows.some(
        (b: SiteBlueprint) => b.status === 'pending' || b.status === 'submitted',
      );
      return inFlight ? 5000 : false;
    },
  });
}

export function useCreateSiteBlueprint() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      siteId,
      file,
      label,
    }: {
      siteId: number;
      file: File;
      label: string;
    }) => createSiteBlueprint(siteId, file, label),
    onSuccess: (_, { siteId }) => {
      queryClient.invalidateQueries({ queryKey: blueprintKeys.list(siteId) });
    },
  });
}

export function useUpdateSiteBlueprint() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      blueprintId,
      patch,
    }: {
      siteId: number;
      blueprintId: number;
      patch: Partial<Pick<SiteBlueprint, 'label'>>;
    }) => updateSiteBlueprint(blueprintId, patch),
    onSuccess: (_, { siteId }) => {
      queryClient.invalidateQueries({ queryKey: blueprintKeys.list(siteId) });
    },
  });
}

export function useReplaceSiteBlueprintFile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      blueprintId,
      file,
    }: {
      siteId: number;
      blueprintId: number;
      file: File;
    }) => replaceSiteBlueprintFile(blueprintId, file),
    onSuccess: (_, { siteId }) => {
      queryClient.invalidateQueries({ queryKey: blueprintKeys.list(siteId) });
    },
  });
}

export function useDeleteSiteBlueprint() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      blueprintId,
    }: {
      siteId: number;
      blueprintId: number;
    }) => deleteSiteBlueprint(blueprintId),
    onSuccess: (_, { siteId }) => {
      queryClient.invalidateQueries({ queryKey: blueprintKeys.list(siteId) });
    },
  });
}
